import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../lib/supabase';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWebsites();
  }, []);

  const fetchWebsites = async () => {
    try {
      const { data: websitesData, error } = await supabase
        .from('websites_seo_7k9m2x')
        .select(`
          *,
          pages_seo_7k9m2x (
            *,
            key_phrases_seo_7k9m2x (*)
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedWebsites = websitesData.map(website => ({
        ...website,
        pages: website.pages_seo_7k9m2x.map(page => ({
          ...page,
          keyPhrases: page.key_phrases_seo_7k9m2x
        }))
      }));

      setWebsites(formattedWebsites);
    } catch (error) {
      console.error('Error fetching websites:', error);
    } finally {
      setLoading(false);
    }
  };

  const addWebsite = async (website) => {
    try {
      const { data, error } = await supabase
        .from('websites_seo_7k9m2x')
        .insert([website])
        .select()
        .single();

      if (error) throw error;

      const newWebsite = { ...data, pages: [] };
      setWebsites(prev => [newWebsite, ...prev]);
      return newWebsite;
    } catch (error) {
      console.error('Error adding website:', error);
      throw error;
    }
  };

  const updateWebsite = async (id, updates) => {
    try {
      const { error } = await supabase
        .from('websites_seo_7k9m2x')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setWebsites(prev => prev.map(website => 
        website.id === id ? { ...website, ...updates } : website
      ));
    } catch (error) {
      console.error('Error updating website:', error);
      throw error;
    }
  };

  const deleteWebsite = async (id) => {
    try {
      const { error } = await supabase
        .from('websites_seo_7k9m2x')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setWebsites(prev => prev.filter(website => website.id !== id));
    } catch (error) {
      console.error('Error deleting website:', error);
      throw error;
    }
  };

  const addPage = async (websiteId, pageData) => {
    try {
      const { keyPhrases, ...page } = pageData;

      const { data: pageResult, error: pageError } = await supabase
        .from('pages_seo_7k9m2x')
        .insert([{ ...page, website_id: websiteId }])
        .select()
        .single();

      if (pageError) throw pageError;

      // Add key phrases
      if (keyPhrases && keyPhrases.length > 0) {
        const keyPhrasesData = keyPhrases.map(phrase => ({
          page_id: pageResult.id,
          phrase: phrase.trim()
        }));

        const { error: keyPhrasesError } = await supabase
          .from('key_phrases_seo_7k9m2x')
          .insert(keyPhrasesData);

        if (keyPhrasesError) throw keyPhrasesError;
      }

      await fetchWebsites();
      return pageResult;
    } catch (error) {
      console.error('Error adding page:', error);
      throw error;
    }
  };

  const updatePage = async (websiteId, pageId, updates) => {
    try {
      const { keyPhrases, ...pageUpdates } = updates;

      const { error: pageError } = await supabase
        .from('pages_seo_7k9m2x')
        .update(pageUpdates)
        .eq('id', pageId);

      if (pageError) throw pageError;

      // Update key phrases
      if (keyPhrases !== undefined) {
        // Delete existing key phrases
        await supabase
          .from('key_phrases_seo_7k9m2x')
          .delete()
          .eq('page_id', pageId);

        // Add new key phrases
        if (keyPhrases.length > 0) {
          const keyPhrasesData = keyPhrases.map(phrase => ({
            page_id: pageId,
            phrase: phrase.trim()
          }));

          const { error: keyPhrasesError } = await supabase
            .from('key_phrases_seo_7k9m2x')
            .insert(keyPhrasesData);

          if (keyPhrasesError) throw keyPhrasesError;
        }
      }

      await fetchWebsites();
    } catch (error) {
      console.error('Error updating page:', error);
      throw error;
    }
  };

  const deletePage = async (websiteId, pageId) => {
    try {
      const { error } = await supabase
        .from('pages_seo_7k9m2x')
        .delete()
        .eq('id', pageId);

      if (error) throw error;

      await fetchWebsites();
    } catch (error) {
      console.error('Error deleting page:', error);
      throw error;
    }
  };

  const shareProject = async (websiteId, selectedPages) => {
    try {
      const shareId = Math.random().toString(36).substr(2, 12);

      const { error } = await supabase
        .from('shared_projects_seo_7k9m2x')
        .insert([{
          share_id: shareId,
          website_id: websiteId,
          selected_pages: selectedPages
        }]);

      if (error) throw error;

      return shareId;
    } catch (error) {
      console.error('Error sharing project:', error);
      throw error;
    }
  };

  const getSharedProject = async (shareId) => {
    try {
      const { data, error } = await supabase
        .from('shared_projects_seo_7k9m2x')
        .select(`
          *,
          websites_seo_7k9m2x (
            *,
            pages_seo_7k9m2x (
              *,
              key_phrases_seo_7k9m2x (*)
            )
          )
        `)
        .eq('share_id', shareId)
        .single();

      if (error) throw error;

      const website = data.websites_seo_7k9m2x;
      const selectedPageIds = data.selected_pages;

      const selectedPages = website.pages_seo_7k9m2x
        .filter(page => selectedPageIds.includes(page.id))
        .map(page => ({
          ...page,
          keyPhrases: page.key_phrases_seo_7k9m2x
        }));

      return {
        id: data.share_id,
        website: {
          name: website.name,
          logo: website.logo,
          url: website.url
        },
        pages: selectedPages,
        createdAt: data.created_at
      };
    } catch (error) {
      console.error('Error fetching shared project:', error);
      return null;
    }
  };

  return (
    <DataContext.Provider value={{
      websites,
      loading,
      addWebsite,
      updateWebsite,
      deleteWebsite,
      addPage,
      updatePage,
      deletePage,
      shareProject,
      getSharedProject,
      fetchWebsites
    }}>
      {children}
    </DataContext.Provider>
  );
};